import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { api } from '@/services/api';
import { ModelNameInputProps } from '../../types';
import '../styles.css';
import './styles.css';

const ModelNameInput: React.FC<ModelNameInputProps> = ({
  value,
  onChange,
  onSelect,
  required = false,
  placeholder = 'Enter model name',
  label = 'Model Name',
  id = 'modelName',
  className = '',
  disabled = false,
  error,
  testId
}) => {
  const [modelNames, setModelNames] = useState<string[]>([]);
  const [filteredModelNames, setFilteredModelNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Common model names to suggest if no previous models exist
  const commonModelNames = [
    'GPT-4',
    'GPT-3.5',
    'Claude 3 Opus',
    'Claude 3 Sonnet',
    'Claude 3 Haiku',
    'Gemini Pro',
    'Gemini Ultra',
    'Llama 3',
    'Mistral Large',
    'Mistral Medium'
  ];

  // Fetch all model names on component mount
  useEffect(() => {
    const fetchModelNames = async () => {
      try {
        setIsLoading(true);
        const data = await api.evaluations.getModelNames();
        setModelNames(data);
      } catch (error) {
        console.error('Error fetching model names:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchModelNames();
  }, []);

  // Filter model names based on input value
  useEffect(() => {
    if (!value.trim()) {
      setFilteredModelNames([]);
      return;
    }

    const filtered = modelNames.length > 0
      ? modelNames.filter(modelName => 
          modelName.toLowerCase().includes(value.toLowerCase())
        )
      : commonModelNames.filter(name => 
          name.toLowerCase().includes(value.toLowerCase())
        );
    
    setFilteredModelNames(filtered);
    setFocusedIndex(-1);
  }, [value, modelNames]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (modelName: string) => {
    onChange(modelName);
    onSelect?.(modelName);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredModelNames.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredModelNames.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredModelNames.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleSuggestionClick(filteredModelNames[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const suggestionsToShow = filteredModelNames;
  const inputId = `${id}-input`;
  const listboxId = `${id}-listbox`;

  return (
    <div 
      className={`input-container ${className}`}
      data-testid={testId}
    >
      <label 
        htmlFor={inputId}
        className="input-label"
      >
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          id={inputId}
          className={`input-field ${error ? 'has-error' : ''}`}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          aria-expanded={showSuggestions}
          aria-controls={showSuggestions ? listboxId : undefined}
          aria-activedescendant={
            focusedIndex >= 0 ? `${id}-option-${focusedIndex}` : undefined
          }
          role="combobox"
        />
        {isLoading && <div className="loading-indicator" role="status" aria-label="Loading suggestions" />}
        {error && (
          <div 
            id={`${inputId}-error`} 
            className="error-text" 
            role="alert"
          >
            {error}
          </div>
        )}
        {showSuggestions && suggestionsToShow.length > 0 && (
          <div 
            ref={suggestionsRef}
            id={listboxId}
            className="suggestions-container"
            role="listbox"
          >
            {suggestionsToShow.map((modelName, index) => (
              <div 
                key={modelName}
                id={`${id}-option-${index}`}
                className={`suggestion-item ${focusedIndex === index ? 'focused' : ''}`}
                onClick={() => handleSuggestionClick(modelName)}
                onMouseEnter={() => setFocusedIndex(index)}
                role="option"
                aria-selected={focusedIndex === index}
              >
                {modelName}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelNameInput; 